/**
 * Autocomplete tag-input for composing notification recipient lists.
 *
 * @module components/settings/TagInput
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from 'prop-types';
import useAutocomplete from '@material-ui/lab/useAutocomplete';
import NoSsr from '@material-ui/core/NoSsr';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import styled from 'styled-components';

/**
 * `InputWrapper` is a styled `div` component that provides a visually distinctive container
 * for input fields with specific styling for default, hovered, and focused states.
 *
 * Features:
 * - Rounded corners and a solid border for default appearance.
 * - Background color set to white for contrast with surrounding elements.
 * - Responsive flexible box layout with support for wrapping child elements.
 * - Hover state alters the border color to provide visual feedback.
 * - Focused state highlights the component with a different border color and shadow effect.
 * - Child `<input>` elements inherit specific styles like padding, font size, and layout settings
 *   for consistent appearance and usability.
 */
const InputWrapper = styled('div')`
    border: 1px solid #d9d9d9;
    background-color: #fff;
    border-radius: 4px;
    padding: 6px;
    display: flex;
    flex-wrap: wrap;

    &:hover {
        border-color: #40a9ff;
    }

    &.focused {
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    & input {
        font-size: 14px;
        height: 30px;
        box-sizing: border-box;
        padding: 4px 6px;
        width: 0;
        min-width: 30px;
        flex-grow: 1;
        border: 0;
        margin: 0;
        outline: 0;
    }
`;

/**
 * A styled reusable Tag component.
 *
 * This component is designed to display a label with an optional delete action.
 * It uses a styled wrapper and incorporates semantic elements for better accessibility
 * and interaction handling.
 *
 * Properties:
 * - `label` (string): The text to display within the tag.
 * - `onDelete` (function): Callback function to be invoked when the delete action is triggered.
 * - `props` (object): Additional properties to be spread onto the root element.
 *
 * Styles:
 * - Displays as a flex container with horizontal alignment.
 * - Dynamically adjusts padding, margin, and line-height for compact styling.
 * - Incorporates a close icon for delete functionality with customizable styles.
 *
 * Accessibility:
 * - The delete icon is made interactive and visually distinct.
 *
 * Interaction:
 * - Includes stylistic changes when in focus.
 * - Ensures overflow text within the label is visually constrained with ellipsis.
 */
const Tag = styled(({ label, onDelete, ...props }) => (
    <div {...props}>
        <span>{label}</span>
        <CloseIcon onClick={onDelete} />
    </div>
))`
    display: flex;
    align-items: center;
    height: 24px;
    margin: 2px;
    line-height: 22px;
    background-color: #4B6AA3;
    border: 1px solid #e8e8e8;
    color: #fff;
    border-radius: 5px;
    box-sizing: content-box;
    padding: 1px 3px 0px 10px;
    font-size: 12px;
    outline: 0;
    overflow: hidden;

    &:focus {
        border-color: #40a9ff;
        background-color: #e6f7ff;
    }

    & span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    & svg {
        font-size: 8px;
        cursor: pointer;
        padding: 4px;
        background: #8197BF;
        margin: 6px;
        border-radius: 2px;
    }
`;

/**
 * Styled component representing a listbox, typically used for rendering options in a dropdown or autocomplete component.
 *
 * The `Listbox` component is designed as an unordered list (`ul`) with custom styles applied for better user experience.
 * It supports dynamic width, has a fixed maximum height, and includes scroll support for overflow content.
 *
 * Features:
 * - Dynamically sets width based on the `width` property provided via props.
 * - Styled with margin, padding, background color, border radius, and shadow for visual enhancement.
 * - Supports keyboard and mouse interaction styling to indicate focus and selection states.
 * - Configures aria attributes and data attributes for accessibility compliance and focus state indication.
 *
 * States:
 * - `aria-selected='true'`: Highlights the selected list item with a bold font and specific background color.
 * - `data-focus='true'`: Applies styles for the currently focused item, including background color and cursor change.
 *
 * Child Elements:
 * - Each `li` element represents an item in the listbox with padded content.
 * - Each `li` contains child `span` and optional `svg` elements for structure and visual indicators.
 *
 * Notes:
 * - It is expected that this component is used within a controlled environment where its props are dynamically managed.
 * - The component makes use of CSS selectors to apply styles for specific states, ensuring a polished user experience.
 */
const Listbox = styled('ul')`
    width: ${(props) => props.width};
    margin: 2px 0 0;
    padding: 0;
    position: absolute;
    list-style: none;
    background-color: #fff;
    overflow: auto;
    max-height: 250px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1;

    & li {
        padding: 5px 12px;
        display: flex;

        & span {
            flex-grow: 1;
        }

        & svg {
            color: transparent;
        }
    }

    & li[aria-selected='true'] {
        background-color: #fafafa;
        font-weight: 600;

        & svg {
            color: #1890ff;
        }
    }

    & li[data-focus='true'] {
        background-color: #e6f7ff;
        cursor: pointer;

        & svg {
            color: #000;
        }
    }
`;

/**
 * Represents a TagInput component for selecting multiple groups or users.
 *
 * This component uses the `useAutocomplete` hook to provide an interactive
 * input with tagging functionality. It allows users to select multiple options
 * from a list and displays them as tags. The options can be groups or users and
 * are determined based on the provided `userData` and `selectGroups` props.
 *
 * @param {Object} props - The props for the TagInput component.
 * @param {Array} props.userData - The user data object containing group association details.
 * @param {Array} props.selectGroups - An array of selectable groups or users to display as options.
 * @param {Function} props.handleGroupChange - A callback function triggered when the selection changes.
 * @param {string | number} [props.width] - Optional width of the options dropdown, defaults to '300px' if not provided.
 */
const TagInput = ({
    userData, selectGroups, handleGroupChange, width,
}) => {
    const filteredArray = userData
        ? selectGroups.filter((group) => userData.groups.includes(group._id)) : [];
    const {
        getRootProps,
        getInputProps,
        getTagProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        value,
        focused,
        setAnchorEl,
    } = useAutocomplete({
        id: 'customized-hook-demo',
        defaultValue: filteredArray,
        multiple: true,
        options: selectGroups,
        getOptionLabel: (option) => (option.name ? option.name : `${option.first_name} ${option.last_name}`),
    });
    return (
        <NoSsr>
            <div>
                <div {...getRootProps()}>
                    <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''} name="groups" onBlur={() => handleGroupChange(value)}>
                        {value.map((option, index) => (
                            <Tag
                                label={option.name
                                    ? option.name : `${option.first_name} ${option.last_name}`}
                                {...getTagProps({ index })}
                                key={option.id}
                            />
                        ))}

                        <input {...getInputProps()} />
                    </InputWrapper>
                </div>
                {groupedOptions.length > 0 ? (
                    <Listbox {...getListboxProps()} width={width || '300px'}>
                        {groupedOptions.map((option, index) => (
                            <li {...getOptionProps({ option, index })}
                                key={option._id}
                            >
                <span>
                  {option.name
                      ? option.name : `${option.first_name} ${
                          option.last_name}`}

                </span>
                                <CheckIcon fontSize="small" />
                            </li>
                        ))}
                    </Listbox>
                ) : null}
            </div>
        </NoSsr>
    );
};

TagInput.propTypes = {
    userData: PropTypes.object,
    selectGroups: PropTypes.array,
    handleGroupChange: PropTypes.func,
    width: PropTypes.string,
};

export default TagInput;
